import React, { FC, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    StyledMap,
    StyledContentMap,
    StyledMapList,
    StyledSearchWrapper,
    StyledSearchLocation,
    StyledButton,
} from './styled';
import MapHelper from './map-helper/map-helper';
import { StoreListUl } from './components';
import { fitBounds } from './utils/fitBounds';
import { IM82FindDealerMapBlockViewModel } from './models';
import markerSelected from '$assets/pinimerco_black.svg';
import markerOption from '$assets/pinimerco.svg';
import markerUser from '$assets/marker-user.svg';
import { generateMapMarkers } from './utils/generateMapMarkers';
import { Store } from '~/lib/data-contract/salesforce';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import { SearchInput } from '~/shared/components';
import { useTranslation } from '~/shared/utils/translation';
import { filterStores } from '~/shared/utils';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { default as Location } from '~/icons/location.svg';
import { StyledNoStoreFound, StyledUlListSelector } from './components/styled';
import { haversineDistance } from '~/shared/utils/haversineDistance';

type Props = {
    storeList: Store[];
};

type ComponentProps = IM82FindDealerMapBlockViewModel & Props;

export const StoreMapList: FC<ComponentProps> = ({ storeList }) => {
    const [stores, setStores] = useState(storeList);

    const [searchQuery, setSearchQuery] = useState('');

    const [map, setMap] = useState<google.maps.Map | null>(null);

    const { deliveryAddress: selectedPin, setDeliveryAddress } = useCheckoutState();

    const { translate } = useTranslation();

    const isMobile = useIsMobile();

    const limit = (stores: Store[]) => {
        return isMobile && searchQuery ? 3 : stores.length;
    };

    const mapCircle = useRef<google.maps.Circle | null>(null);
    const mapUserMarker = useRef<google.maps.Marker | null>(null);

    const removeUserMarker = () => {
        if (mapCircle.current && mapUserMarker.current && map) {
            mapCircle.current.setMap(null);
            mapUserMarker.current.setMap(null);

            mapCircle.current = null;
            mapUserMarker.current = null;
        }
    };

    const selectOptionStore = (storeOption: any) => {
        const clickLocal = {
            lat: storeOption.latitude || storeOption.lat,
            lng: storeOption.longitude || storeOption.lng,
        };

        if (map) {
            map.setZoom(15);
            map.panTo(clickLocal);
        }

        setDeliveryAddress(storeOption);
    };

    useEffect(() => {
        let filteredStores = storeList;

        if (searchQuery.length > 2) {
            filteredStores = filterStores({
                stores: storeList,
                searchQuery,
            });

            setStores(filteredStores);
        } else {
            filteredStores = storeList;
            setStores(storeList);
        }

        if (filteredStores.length && map) {
            removeUserMarker();

            fitBounds(map, generateMapMarkers(filteredStores), limit(filteredStores));
        }
    }, [searchQuery]);

    /**
     * When map is loaded, set it in state,
     * and if mapmarkers already are theye
     */
    const mapLoaded = useCallback(
        (map) => {
            setMap(map);
            if (storeList.length) {
                fitBounds(map, generateMapMarkers(storeList), limit(storeList));
            }
        },
        [storeList],
    );

    const handleFilterByDistance = () => {
        removeUserMarker();

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => {
                const { coords } = position;
                const markerDistances: { [key: string]: number } = {};

                const filteredList = storeList
                    .filter((marker) => {
                        const distance = haversineDistance(
                            {
                                lat: coords.latitude,
                                lng: coords.longitude,
                            },
                            {
                                latitude: marker?.latitude ?? 0,
                                longitude: marker?.longitude ?? 0,
                            },
                        );

                        const isNear = distance <= 10; //10km

                        if (isNear) {
                            markerDistances[marker.id] = distance;
                        }

                        return isNear;
                    })
                    .sort((a, b) => {
                        if (markerDistances[a.id] < markerDistances[b.id]) {
                            return -1;
                        } else if (markerDistances[a.id] > markerDistances[b.id]) {
                            return 1;
                        }

                        return 0;
                    });

                setStores(filteredList);

                if (filteredList.length && map) {
                    const userCenter = new google.maps.LatLng(coords.latitude, coords.longitude);

                    mapCircle.current = new google.maps.Circle({
                        strokeColor: '#F68A20',
                        strokeOpacity: 0.3,
                        strokeWeight: 2,
                        fillColor: '#F68A20',
                        fillOpacity: 0.1,
                        radius: 10000,
                        map,
                        center: userCenter,
                    });

                    mapUserMarker.current = new google.maps.Marker({
                        position: userCenter,
                        icon: {
                            url: markerUser,
                        },
                        map,
                    });

                    fitBounds(map, generateMapMarkers(filteredList), limit(filteredList));
                }
            });
        }
    };

    const handleResetList = () => {
        removeUserMarker();
        setStores(storeList);
        setSearchQuery('');

        if (storeList.length && map) {
            fitBounds(map, generateMapMarkers(storeList), limit(storeList));
        }
    };

    const mapCenter = useMemo(() => {
        // Return default center if no map or no stores
        if (!map || !stores.length) {
            return {
                lat: 55.970546, // Denmark Center location
                lng: 10.103119,
            };
        }

        const bounds = new google.maps.LatLngBounds();
        const locations = generateMapMarkers(stores);

        locations.forEach((m) => {
            const markerBounds = new google.maps.LatLng(m.lat, m.lng);
            bounds.extend(markerBounds);
        });

        return bounds.getCenter().toJSON();
    }, [stores, map]);

    const mapMarkerList = useMemo(() => generateMapMarkers(storeList), [storeList]);

    return useMemo(
        () => (
            <StyledContentMap>
                <StyledMapList>
                    <>
                        <StyledSearchWrapper>
                            <SearchInput
                                onChange={(e) => setSearchQuery(e.target.value)}
                                value={searchQuery}
                                placeholder={translate('findStore.searchZipCode')}
                                data-testid="searchZipCode"
                            />

                            <StyledSearchLocation>
                                <StyledButton onClick={handleFilterByDistance}>
                                    <Location />
                                    {translate('findStore.closeToMyLocation')}
                                </StyledButton>

                                {stores.length !== storeList.length && stores.length !== 0 && (
                                    <StyledButton onClick={handleResetList}>
                                        {translate('findStore.clear')}
                                    </StyledButton>
                                )}
                            </StyledSearchLocation>
                        </StyledSearchWrapper>

                        {stores.length ? (
                            <StoreListUl
                                storeList={stores}
                                selectedPin={selectedPin}
                                selectOption={selectOptionStore}
                            />
                        ) : (
                            <StyledUlListSelector>
                                <StyledNoStoreFound>
                                    {translate('findStore.noStoresFound')}

                                    <StyledButton onClick={handleResetList}>
                                        {translate('findStore.clear')}
                                    </StyledButton>
                                </StyledNoStoreFound>
                            </StyledUlListSelector>
                        )}
                    </>
                </StyledMapList>

                <StyledMap data-testid="map">
                    <MapHelper
                        markers={mapMarkerList}
                        markerIcon={markerOption}
                        selectedMarkerIcon={markerSelected}
                        onMapLoadCallback={mapLoaded}
                        onPinClick={selectOptionStore}
                        center={mapCenter}
                        clusterOptions={{
                            maxZoom: 12,
                            clusterClass: 'mapHelper__cluster',
                            averageCenter: true,
                            styles: [
                                {
                                    textColor: '#000',
                                    fontFamily: 'inherit',
                                    url: '',
                                    height: 40,
                                    width: 40,
                                },
                            ],
                        }}
                        selectedPin={selectedPin}
                    />
                </StyledMap>
            </StyledContentMap>
        ),
        [storeList, selectedPin, searchQuery, stores, map],
    );
};
